import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { UserInfo, VisualCache } from "../helper/Context";
import { Form, Button, Row, Col, Modal, Image, Card, Spinner as RBSpinner, OverlayTrigger, Tooltip, Popover } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faUpload,
  faSearch,
  faLink,
  faSave,
  faCamera,
  faLock,
  faQrcode,
  faCopy,
  faEye,
  faArrowLeft,
  faCheck,
  faImages,
  faTrashCan,
  faFileText,
  faFileExport,
  faCommentDots,
  faInfo,
} from "@fortawesome/free-solid-svg-icons";
import { faPaperPlane } from "@fortawesome/free-regular-svg-icons";
import { APIAddEditPresentation, APIGetVisualizations } from "../helper/APIFunctions";
import { useNavigate, useLocation } from "react-router-dom";
import { usePrompt } from '../hooks/usePrompt'; // Import the new hook
import CreateVisualFlow from "../components/CreateVisualFlow";
import VisualPickerModal from '../components/VisualPickerModal';
import CustomTextSlideEditor from "../components/CustomTextSlideEditor";
import ShareModal from "../components/VisualModals/Share";
import { generateSlideHtml } from '../helper/SlideUtils';
import { isMobile } from "../helper/Utils";
import debounce from 'lodash.debounce';

const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".mkv", ".avi", ".flv"]; // for video preview detection

// Add slide base dimensions
const SLIDE_BASE_WIDTH = 1280;
const SLIDE_BASE_HEIGHT = 720;


const normalizePosition = (pos) => ({
  // x, y are distances from top-left of slide to top-left of element's bounding box
  x: pos.x / SLIDE_BASE_WIDTH,
  y: pos.y / SLIDE_BASE_HEIGHT,
  // width and height are the dimensions of the element's bounding box
  width: pos.width / SLIDE_BASE_WIDTH,
  height: pos.height === 'auto' ? 'auto' : pos.height / SLIDE_BASE_HEIGHT
});
const unnormalizePosition = (pos) => ({
  x: pos.x * SLIDE_BASE_WIDTH,
  y: pos.y * SLIDE_BASE_HEIGHT,
  width: pos.width * SLIDE_BASE_WIDTH,
  height: pos.height === 'auto' ? 'auto' : pos.height * SLIDE_BASE_HEIGHT
});


// Add format constants
const PRESENTATION_FORMATS = {
  LEGACY: 'legacy',
  MODERN: 'modern'
};

// Helper to determine presentation format
const getPresentationFormat = (presentation) => {
  if (presentation.format) { return presentation.format; }

  if (!presentation) return PRESENTATION_FORMATS.MODERN; // Default to modern for new presentations

  // Check if any slide has the modern format indicators
  const hasModernSlides = presentation.slides?.some(slide =>
    slide.type === 'custom_text' || slide.slideState || slide.elements
  );

  return hasModernSlides ? PRESENTATION_FORMATS.MODERN : PRESENTATION_FORMATS.LEGACY;
};

const defaultPresentation = (userInfo) => {
  return {
    title: "New Presentation",
    description: "",
    format: PRESENTATION_FORMATS.MODERN, // Add format indicator
    slides: [{
      type: "custom_text",
      slide_type: "custom_text",
      id: `new-slide-${Date.now()}`,
      slideState: {
        textboxes: [
          {
            id: 1, x: 50, y: 50, width: 400, height: 'auto', text: 'Click to add title',
            fontSize: 48, fontFamily: 'Arial', fontColor: '#000000', isBold: true, isItalic: false, isUnderline: false,
          },
          {
            id: 2, x: 50, y: 150, width: 400, height: 'auto', text: 'Click to add subtitle',
            fontSize: 24, fontFamily: 'Arial', fontColor: '#333333', isBold: false, isItalic: false, isUnderline: false,
          },
        ],
        images: [],
        iframes: [],
        background: '#ffffff',
      },
      header: "Custom Text Slide",
      thumbnail: "https://cdn-icons-png.flaticon.com/512/888/888834.png"
    }],
    privacy: "unlisted",
    show_visual_qrs: false,
    enable_tts: false,
    auto_advance: 0,
    branding_profile: userInfo?.branding_profiles?.[0]?.id || null,
  };
};

const CreatePresentationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useContext(UserInfo);
  const { visCache } = useContext(VisualCache);
  const [presentation, setPresentation] = useState(normalizePresentation(location.state?.presentation?.id ? location.state.presentation : defaultPresentation(userInfo)));
  const [showAddSlideModal, setShowAddSlideModal] = useState(false);
  const [modalView, setModalView] = useState("options"); // 'options', 'create', 'my-visuals'
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [presentations, setPresentations] = useState([]);
  const [isLoadingPresentations, setIsLoadingPresentations] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleInput, setTitleInput] = useState('Untitled');
  const [presentationUrl, setPresentationUrl] = useState(null);
  const [presentationQr, setPresentationQr] = useState(null);
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [slideToDelete, setSlideToDelete] = useState(null);
  const [draggedSlideIndex, setDraggedSlideIndex] = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null);
  const slideRefs = useRef([]);
  const fileInputRef = useRef(null);
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [debouncedSlides, setDebouncedSlides] = useState(presentation.slides);
  const [hoveredPopupIndex, setHoveredPopupIndex] = useState(null);
  const [thumbnailHtmlCache, setThumbnailHtmlCache] = useState({});
  
  // Copy/paste state for slides
  const [copiedSlide, setCopiedSlide] = useState(null);
  const [copiedSlideIndex, setCopiedSlideIndex] = useState(null);

  // Add a ref to access the current slide editor state
  const currentSlideEditorRef = useRef(null);


  // Add a ref to store all slide editors
  const slideEditorsRef = useRef({});

  // Add state to track selected iframe
  const [selectedIframe, setSelectedIframe] = useState(null);

  // Add callback to handle iframe selection
  const handleIframeSelect = useCallback((iframeData) => {
    // Only set iframe data if we have a valid iframe
    if (iframeData && iframeData.src) {
      setSelectedIframe({
        id: iframeData.id,
        isSnorkle: iframeData.src.includes('vis.snorkle')
      });
    } else {
      // Clear selection if no iframe or invalid data
      setSelectedIframe(null);
    }
  }, []);

  const isOrg = false;

  useEffect(() => {
    if (location.state?.presentation?.id) {
      const pres = location.state.presentation;
      setPresentation(normalizePresentation(pres));
      setLastSavedPresentation(normalizePresentation(pres));
      setHasUnsavedChanges(false);
      setShowEntryModal(false);
      setPresentationUrl(pres.url || "");
      setPresentationQr(pres.qr_url || "");
      setSelectedSlideIndex(0);
      setTitleInput(pres.title || "New Presentation");
    }
  }, [location]);

  useEffect(() => {
    const debouncedUpdate = debounce((newSlides) => {
      setDebouncedSlides(newSlides);
    }, 500);

    if (JSON.stringify(presentation.slides) !== JSON.stringify(debouncedSlides)) {
      debouncedUpdate(presentation.slides);
    }

    return () => {
      debouncedUpdate.cancel();
    };
  }, [presentation.slides]);

  useEffect(() => {
    if (slideRefs.current[selectedSlideIndex]) {
      slideRefs.current[selectedSlideIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedSlideIndex]);

  useEffect(() => {
    const newCache = {};
    debouncedSlides.forEach((slide, index) => {
      if (slide.slideState) {
        newCache[index] = {
          noPopup: generateSlideHtml(slide.slideState, visCache, true, false),
          withPopup: slide.slideState.showPopup ? generateSlideHtml(slide.slideState, visCache, true, true) : null
        };
      }
    });
    setThumbnailHtmlCache(newCache);
  }, [debouncedSlides, visCache]);

  useEffect(() => {
    // This effect handles the visibility of the close button on the parent modal.
    // It applies the style whenever this specific modal is open.
    if (showAddSlideModal) {
      const styleId = 'force-white-parent-modal-close-button';
      if (document.getElementById(styleId)) return;

      const style = document.createElement('style');
      style.id = styleId;
      // Target the close button within the specific #add-slide-modal to avoid side effects.
      style.innerHTML = `
        #add-slide-modal .modal-header .btn-close {
          filter: brightness(0) invert(1) !important;
        }
      `;
      document.head.appendChild(style);

      // Cleanup function to remove the style when the modal closes.
      return () => {
        const styleElement = document.getElementById(styleId);
        if (styleElement) {
          document.head.removeChild(styleElement);
        }
      };
    }
  }, [showAddSlideModal]);

  useEffect(() => {
    if (isMobile()) {
      setShowMobileWarning(true);
    }
  }, []);

  const resetToNewPresentation = () => {
    const newPresentationState = defaultPresentation(userInfo);
    setPresentation(newPresentationState);
    setLastSavedPresentation(newPresentationState); // No unsaved changes on a new presentation
    setPresentationUrl(null);
    setPresentationQr(null);
    setSelectedSlideIndex(0);
    setTitleInput('New Presentation');
    setShowEntryModal(false); // We are explicitly creating new, so don't show the modal
    setHasUnsavedChanges(true); // It's a new, unsaved presentation
  };

  // Use the new usePrompt hook
  usePrompt({
    when: hasUnsavedChanges,
    message: "You have unsaved changes. Do you want to leave without saving?",
  });

  // Fetch presentations for edit option (mock fallback)
  const fetchPresentations = useCallback(() => {
    setIsLoadingPresentations(true);
    APIGetVisualizations(
      (body) => {
        let presentationsList = [];
        if (body.presentations && Array.isArray(body.presentations) && body.presentations.length > 0) {
          presentationsList = body.presentations;
        } else {
          const all = body.visuals || body;
          presentationsList = (all || []).filter(
            v => v.type === "snorkle-presentation" || v.type === "presentation" || v.is_presentation || Array.isArray(v.slides) || v.type === "slides"
          )
        }
        setPresentations(presentationsList.filter(v => v.type !== "scrollytelling"));
        setIsLoadingPresentations(false);
      },
      (err) => {
        setPresentations([]);
        setIsLoadingPresentations(false);
      },
      visCache
    );
  }, [visCache]);

  // Update titleInput when presentation changes
  useEffect(() => {
    setTitleInput(presentation.title || 'Untitled');
  }, [presentation.title]);

  // Track unsaved changes only if there are actual changes
  const [lastSavedPresentation, setLastSavedPresentation] = useState(null);
  useEffect(() => {
    if (!lastSavedPresentation) return;
    const pStr = JSON.stringify(presentation);
    const lspStr = JSON.stringify(lastSavedPresentation);
    setHasUnsavedChanges(pStr !== lspStr);
  }, [presentation, lastSavedPresentation]);

  // Set default thumbnail from first slide
  useEffect(() => {
    if (!presentation.thumbnail && presentation.slides && presentation.slides.length > 0) {
      const firstSlideWithThumb = presentation.slides.find(s => s.thumbnail);
      if (firstSlideWithThumb) {
        setPresentation(prev => ({ ...prev, thumbnail: firstSlideWithThumb.thumbnail }));
      }
    }
  }, [presentation.slides, presentation.thumbnail]);

  // Helper to normalize slides and set URL/QR
  function normalizePresentation(pres) {
    if (getPresentationFormat(pres) === PRESENTATION_FORMATS.LEGACY) {
      return {
        ...pres,
        slides: (pres.slides || []).map(slide => {
          let visual = null;
          if (visCache && slide.visual_id && visCache[slide.visual_id]) {
            visual = visCache[slide.visual_id];
          }
          return {
            ...visual, // full visual info first
            ...slide,  // slide-specific overrides (title, description, intro, etc.)
            visualization_url: (slide.visualization_url || slide.visual_url || (visual && visual.visualization_url) || ""),
            thumbnail: (slide.thumbnail || (visual && visual.thumbnail) || (slide.slide_type === 'custom_text' && "https://cdn-icons-png.flaticon.com/512/888/888834.png") || ""),
          };
        })
      };
    }
    else {
      for (const slide of pres.slides) {
        slide.slide_type = slide.type;
        
        // If slide already has slideState and no elements, preserve the existing slideState
        if (slide.slideState && !slide.elements) {
          // Keep existing slideState as-is for new presentations
          continue;
        }
        
        slide.slideState = {
          ...slide.slideState,
          images: (slide.elements?.images || []).map(
            img => {
              return {
                ...img,
                x: unnormalizePosition(img.position).x,
                y: unnormalizePosition(img.position).y,
                width: unnormalizePosition(img.position).width,
                height: unnormalizePosition(img.position).height,
                aspectRatio: img.aspectRatio,
                zIndex: img.zIndex,
              };
            }
          ),
          iframes: (slide.elements?.iframes || []).map(iframe => {
            return {
              ...iframe,
              x: unnormalizePosition(iframe.position).x,
              y: unnormalizePosition(iframe.position).y,
              width: unnormalizePosition(iframe.position).width,
              height: unnormalizePosition(iframe.position).height,
              zIndex: iframe.zIndex,
            };
          }),
          textboxes: (slide.elements?.textboxes || []).map(box => {
            return {
              ...box,
              x: unnormalizePosition(box.position).x,
              y: unnormalizePosition(box.position).y,
              width: unnormalizePosition(box.position).width,
              height: unnormalizePosition(box.position).height,
              fontSize: box.style.fontSize,
              fontColor: box.style.fontColor,
              fontFamily: box.style.fontFamily,
              isBold: box.style.isBold,
              text: box.content,
              isItalic: box.style.isItalic,
              isUnderline: box.style.isUnderline,
              linkUrl: box.linkUrl || '',
              zIndex: box.zIndex,
            };
          }),
          background: slide.background || '#ffffff',
          showPopup: slide.popup || false,
          popupTitle: slide.popup?.title || '',
          popupText: slide.popup?.text || '',
          popupFont: slide.popup?.font || 'Arial',
          iframeSettings: slide.iframeSettings || {},
        };
        for (const iframe of slide.elements?.iframes || []) {
          slide.slideState.iframeSettings[iframe.id] = {showQR: iframe.showQR || false};
        }
      }
      return pres;
    }
  }

  // When setting a presentation, always ensure slides is an array and update lastSavedPresentation
  const handleSelectPresentation = (pres) => {
    const presWithSlides = normalizePresentation(pres);
    setPresentation(presWithSlides);
    setLastSavedPresentation(presWithSlides);
    setShowEntryModal(false);
    setPresentationUrl(pres.url || "");
    setPresentationQr(pres.qr_url || "");
  };

  // Track if this is a new presentation (no id)
  const isNewPresentation = !presentation.id;

  // Add save handlers
  const handleSaveSuccess = (res) => {
    setIsSaving(false);
    const savedPres = normalizePresentation(res);

    // Ensure thumbnail is part of the saved state to prevent immediate "unsaved change" state
    if (!savedPres.thumbnail && savedPres.slides && savedPres.slides.length > 0) {
      const firstSlideWithThumb = savedPres.slides.find(s => s.thumbnail);
      if (firstSlideWithThumb) {
        savedPres.thumbnail = firstSlideWithThumb.thumbnail;
      }
    }

    setPresentation(savedPres);
    setLastSavedPresentation(savedPres);
    setHasUnsavedChanges(false);
    // Only set URL/QR on first save (creation)
    if (!presentationUrl && res.url) {
      setPresentationUrl(res.url);
    }
    if (!presentationQr && res.qr_url) {
      setPresentationQr(res.qr_url);
    }
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 2000);
  };

  const handleSaveError = (err) => {
    setIsSaving(false);
    setError("Failed to save presentation. Please try again.");
  };

  // Update the handleSave function
  const handleSave = () => {
    if (isSaving) return;
    setIsSaving(true);
    setError(null);

    // Determine if this is a legacy presentation
    const format = getPresentationFormat(presentation);

    if (format === PRESENTATION_FORMATS.LEGACY) {
      // Handle legacy format saving
      APIAddEditPresentation(
        presentation,
        handleSaveSuccess,
        handleSaveError,
        isOrg,
        "slides"
      );
    } else {
      const data = generatePresentationJson();
      if (presentation.id) {
        data.id = presentation.id;
      }
      APIAddEditPresentation(
        data,
        handleSaveSuccess,
        handleSaveError,
        isOrg,
        "slides"
      );
    }
  };

  // Save button logic: only enabled if there are unsaved changes and at least one slide
  const isSaveDisabled = isSaving || !hasUnsavedChanges || !(presentation.slides && presentation.slides.length > 0);

  const handleAddVisualToSlide = (visual) => {
    const newSlide = {
      visual_id: visual.id,
      thumbnail: visual.thumbnail,
      title: visual.title,
      description: visual.description,
      visualization_url: visual.visualization_url,
      header: visual.title,
      intro: visual.description,
      isPrivate: visual.privacy === "private"
    };

    setPresentation(prev => ({
      ...prev,
      slides: [...prev.slides, newSlide]
    }));

    setHasUnsavedChanges(true);
    // Hide the modal after adding the slide.
    setShowAddSlideModal(false);
    setModalView("options"); // Reset modal view
  };

  const handleVisualSelectedOrCreated = (visual) => {
    handleAddVisualToSlide(visual);
    setShowAddSlideModal(false);
    setModalView("options");
  };

  const handleAddCustomTextSlide = () => {
    // Inherit background from previous slide if exists
    const prevSlideBg = presentation?.slides?.length
      ? presentation.slides[presentation.slides.length - 1]?.slideState?.background
      : '#ffffff';

    const newSlide = {
      type: "custom_text",
      slide_type: "custom_text",
      id: `new-slide-${Date.now()}`,
      slideState: {
        textboxes: [
          {
            id: 1,
            x: 50,
            y: 50,
            width: 400,
            height: 'auto',
            text: 'Click to add title',
            fontSize: 48,
            fontFamily: 'Arial',
            fontColor: '#000000',
            isBold: true,
            isItalic: false,
            isUnderline: false,
          },
          {
            id: 2,
            x: 50,
            y: 150,
            width: 400,
            height: 'auto',
            text: 'Click to add subtitle',
            fontSize: 24,
            fontFamily: 'Arial',
            fontColor: '#333333',
            isBold: false,
            isItalic: false,
            isUnderline: false,
          },
        ],
        images: [],
        iframes: [],
        background: prevSlideBg || '#ffffff',
        // Add popup text options
        showPopup: false,
        popupTitle: '',
        popupText: '',
        // Track QR settings for each iframe (vis.snorkle)
        iframeSettings: {} // Will store {iframeId: {showQR: boolean}}
      },
      header: "Custom Text Slide",
      thumbnail: "https://cdn-icons-png.flaticon.com/512/888/888834.png"
    };

    setPresentation(prev => ({
      ...prev,
      slides: [...(prev.slides || []), newSlide]
    }));

    setSelectedSlideIndex(presentation.slides.length);
    setHasUnsavedChanges(true);
  };

  const handleTitleChange = (index, value) => {
    setPresentation(prev => ({
      ...prev,
      slides: prev.slides.map((slide, i) => i === index ? { ...slide, header: value } : slide)
    }));
  };

  const handleTextChange = (index, value) => {
    setPresentation(prev => ({
      ...prev,
      slides: prev.slides.map((slide, i) => i === index ? { ...slide, intro: value } : slide)
    }));
  };

  // Update the slide state change handler
  const handleSlideStateChange = useCallback((newSlideState) => {
    setPresentation(prev => {
      const updatedSlides = [...prev.slides];
      updatedSlides[selectedSlideIndex] = {
        ...updatedSlides[selectedSlideIndex],
        slideState: newSlideState
      };

      return {
        ...prev,
        slides: updatedSlides
      };
    });
    setHasUnsavedChanges(true); // Mark changes as unsaved
  }, [selectedSlideIndex]);

  // Update the CustomTextSlideEditor rendering
  const onCurrentSlideStateChange = useCallback((newSlideState) => {
    handleSlideStateChange(newSlideState);
  }, [handleSlideStateChange]);

  // Copy slide function
  const handleCopySlide = useCallback((slideIndex) => {
    if (slideIndex >= 0 && slideIndex < presentation.slides.length) {
      const slideToCopy = presentation.slides[slideIndex];
      setCopiedSlide({ ...slideToCopy });
      setCopiedSlideIndex(slideIndex);
    }
  }, [presentation.slides]);

  // Paste slide function
  const handlePasteSlide = useCallback((targetIndex) => {
    if (copiedSlide) {
      setPresentation(prev => {
        const newSlides = [...prev.slides];
        // Insert the copied slide at the target index
        newSlides.splice(targetIndex, 0, { ...copiedSlide });
        return { ...prev, slides: newSlides };
      });
      
      // Update selected slide index to the newly pasted slide
      setSelectedSlideIndex(targetIndex);
      setHasUnsavedChanges(true);
    }
  }, [copiedSlide]);

  // Keyboard shortcuts for copy/paste
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't intercept copy/paste if user is editing text
      const activeElement = document.activeElement;
      const isEditingText = (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable ||
        activeElement.classList?.contains('ProseMirror') ||
        activeElement.closest?.('.ProseMirror')
      );
      
      // Only handle slide-level copy/paste when NOT editing text
      if (!isEditingText && (e.ctrlKey || e.metaKey)) {
        if (e.key === 'c' && selectedSlideIndex !== null) {
          e.preventDefault();
          handleCopySlide(selectedSlideIndex);
        } else if (e.key === 'v' && copiedSlide) {
          e.preventDefault();
          handlePasteSlide(selectedSlideIndex + 1); // Paste after current slide
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedSlideIndex, copiedSlide, handleCopySlide, handlePasteSlide]);

  const renderModalContent = () => {
    if (modalView === 'my-visuals') {
      return (
        <>  </>
      );
    }

    if (modalView === 'create') {
      return (
        <CreateVisualFlow
          onSuccess={handleVisualSelectedOrCreated}
          onCancel={() => setModalView('options')}
          lightTheme={true}
          onlyOneVisual={true}
        />
      );
    }

    // Default to 'options' view
    return (
      <Row className="text-center">
        <Col>
          <Button variant="outline-primary" className="p-4 w-100" onClick={() => setModalView('my-visuals')}>
            <FontAwesomeIcon icon={faImages} size="2x" className="mb-2" />
            <div>Select from Your Visuals</div>
          </Button>
        </Col>
        <Col>
          <Button variant="outline-primary" className="p-4 w-100" onClick={() => setModalView('create')}>
            <FontAwesomeIcon icon={faPlus} size="2x" className="mb-2" />
            <div>Create a New Visual</div>
          </Button>
        </Col>
        <Col>
          <Button variant="outline-primary" className="p-4 w-100" onClick={handleAddCustomTextSlide}>
            <FontAwesomeIcon icon={faFileText} size="2x" className="mb-2" />
            <div>Add Custom Text Slide</div>
          </Button>
        </Col>
      </Row>
    );
  };

  const handleDeleteSlide = (index) => {
    let nextSelectedIndex = selectedSlideIndex;
    setPresentation(prev => {
      const slides = prev.slides.slice();
      slides.splice(index, 1);
      const newLen = slides.length;
      nextSelectedIndex = newLen === 0 ? 0 : Math.min(index, newLen - 1);
      return { ...prev, slides };
    });
    setSelectedSlideIndex(nextSelectedIndex);
    setShowDeleteModal(false);
    setSlideToDelete(null);
  };

  // Keyboard delete support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === "Delete" || e.key === "Backspace") && presentation.slides.length > 0 && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
        setSlideToDelete(selectedSlideIndex);
        setShowDeleteModal(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [presentation.slides, selectedSlideIndex]);

  // Helper to check if a slide is a video
  function isVideoSlide(slide) {
    if (!slide) return false;
    const url = (slide && slide.visualization_url) ? slide.visualization_url : "";
    if (slide.type === "video") return true;
    return videoExtensions.some(ext => url.toLowerCase().endsWith(ext));
  }

  // Add JSON export functionality
  const generatePresentationJson = () => {
    const normalizeUrl = (u) => {
      const raw = (u || '').trim();
      if (!raw) return null;
      if (/^(https?:|mailto:|tel:)/i.test(raw)) return raw;
      return `https://${raw.replace(/^\/+/, '')}`;
    };
    // Helper function to normalize position data

    // Export modern format with all slides
    const exportData = {
      version: '1.0',
      format: PRESENTATION_FORMATS.MODERN,
      title: presentation.title,
      description: presentation.description,
      slides: presentation.slides.map((slide, index) => {
        if (slide.type === 'custom_text' || slide.slide_type === 'custom_text') {
          // Get all elements to sort by z-index
          const allElements = [
            ...(slide.slideState?.images || []).map(img => ({ ...img, elementType: 'image' })),
            ...(slide.slideState?.iframes || []).map(frame => ({ ...frame, elementType: 'iframe' })),
            ...(slide.slideState?.textboxes || []).map(box => ({ ...box, elementType: 'textbox' })),
          ];

          // Sort to reflect render stacking: images (bottom), iframes (middle), textboxes (top)
          const typeOrder = { image: 0, iframe: 1, textbox: 2 };
          const indexInType = (el) => {
            if (el.elementType === 'textbox') return (slide.slideState?.textboxes || []).findIndex(t => t.id === el.id);
            if (el.elementType === 'image') return (slide.slideState?.images || []).findIndex(i => i.id === el.id);
            if (el.elementType === 'iframe') return (slide.slideState?.iframes || []).findIndex(f => f.id === el.id);
            return 0;
          };
          allElements.sort((a, b) => {
            const ao = typeOrder[a.elementType] ?? 0;
            const bo = typeOrder[b.elementType] ?? 0;
            if (ao !== bo) return ao - bo;
            // Prefer explicit stackOrder if present (newest on top)
            if ((a.stackOrder || 0) !== (b.stackOrder || 0)) return (a.stackOrder || 0) - (b.stackOrder || 0);
            return indexInType(a) - indexInType(b);
          });

          // Assign explicit z-indices based on sorted order
          allElements.forEach((element, idx) => {
            element.zIndex = idx + 1;
          });
          const slideData = {
            slide_number: index + 1,
            type: 'custom_text',
            background: slide.slideState?.background || '#ffffff',
            // Add popup settings (include font for full styling)
            popup: slide.slideState?.showPopup ? {
              title: slide.slideState.popupTitle,
              text: slide.slideState.popupText,
              font: slide.slideState.popupFont || 'Arial'
            } : null,
            // Explicit render order (bottom to top)
            renderOrder: allElements.map(el => ({ id: el.id, type: el.elementType, zIndex: el.zIndex })),
            elements: {
              textboxes: allElements
                .filter(el => el.elementType === 'textbox')
                .map(box => {return {
                  id: box.id,
                  type: 'text',
                  content: box.text,
                  html: box.html || '<p>' + box.text + '</p>',
                  position: normalizePosition(box),
                  style: {
                    fontSize: box.fontSize,
                    fontColor: box.fontColor,
                    fontFamily: box.fontFamily,
                    isBold: box.isBold,
                    isItalic: box.isItalic,
                    isUnderline: box.isUnderline
                  },
                  isLink: !!(box.linkUrl && box.linkUrl.trim()),
                  linkUrl: (box.linkUrl || '').trim() || null,
                  linkUrlNormalized: normalizeUrl(box.linkUrl),
                  zIndex: box.zIndex
                }}),
              images: allElements
                .filter(el => el.elementType === 'image')
                .map(img => ({
                  id: img.id,
                  type: 'image',
                  src: img.src.startsWith('data:') ? `image_${img.id}${getFileExtension(img.src)}` : img.src,
                  position: normalizePosition(img),
                  aspectRatio: img.aspectRatio,
                  zIndex: img.zIndex
                })),
              iframes: allElements
                .filter(el => el.elementType === 'iframe')
                .map(frame => ({
                  id: frame.id,
                  type: 'iframe',
                  src: frame.src,
                  position: normalizePosition(frame),
                  zIndex: frame.zIndex,
                  // Add QR settings for vis.snorkle iframes
                  showQR: frame.src.includes('vis.snorkle') ?
                    (slide.slideState?.iframeSettings?.[frame.id]?.showQR ?? false) : undefined,
                  qrImage: frame.qrImage || undefined
                }))
            }
          };
          return slideData;
        } else {
          // Legacy visual slide
          return {
            slide_number: index + 1,
            type: 'legacy_visual',
            url: slide.visualization_url,
            intro: slide.intro,
            qr: slide.qr_url,
            background: '#ffffff' // Default background for legacy visuals
          };
        }
      })
    };
    return exportData;
  };

  const handleExportJSON = () => {
    const exportData = generatePresentationJson();
    // Create and download JSON file
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${presentation.title || 'presentation'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Helper function to get file extension from data URL or file path
  const getFileExtension = (src) => {
    if (src.startsWith('data:')) {
      // Extract extension from data URL mime type
      const match = src.match(/data:image\/(\w+);/);
      return match ? `.${match[1]}` : '.png'; // Default to .png if can't determine
    } else {
      // Extract extension from file path
      const match = src.match(/\.([^.]+)$/);
      return match ? `.${match[1]}` : '.png'; // Default to .png if can't determine
    }
  };

  // Helper function to get element's index in its original array (for z-index calculation)
  const getElementIndex = (element, slideState) => {
    if (element.elementType === 'textbox') {
      return slideState.textboxes.findIndex(t => t.id === element.id);
    } else if (element.elementType === 'image') {
      return slideState.textboxes.length + slideState.images.findIndex(i => i.id === element.id);
    } else if (element.elementType === 'iframe') {
      return slideState.textboxes.length + slideState.images.length + slideState.iframes.findIndex(f => f.id === element.id);
    }
    return 0;
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        hidden
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
              setPresentation(prev => ({ ...prev, thumbnail: reader.result }));
            };
            reader.readAsDataURL(file);
          }
        }}
      />
      <Modal show={showMobileWarning} onHide={() => setShowMobileWarning(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Desktop Recommended</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This feature is best used on Desktop. Mobile support is limited.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowMobileWarning(false)}>
            Understood
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="presentation-builder-container" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 70px)' }}>
        <Modal
          show={showEntryModal}
          backdrop="static"
          keyboard={false}
          centered
        >
          <Modal.Header closeButton style={{ background: '#222', color: '#fff' }} closeVariant="white" onHide={() => setShowEntryModal(false)}>
            <Modal.Title>Start Presentation</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {isLoadingPresentations ? (
              <div className="text-center py-4">
                <RBSpinner animation="border" />
                <div>Loading presentations...</div>
              </div>
            ) : presentations.length === 0 ? (
              <div className="d-grid gap-3">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={resetToNewPresentation}
                >
                  Create New Presentation
                </Button>
                <Button
                  variant="outline-primary"
                  size="lg"
                  onClick={() => {
                    fetchPresentations();
                  }}
                >
                  Edit Existing Presentation
                </Button>
                {presentations.length === 0 && !isLoadingPresentations && (
                  <div className="text-center text-muted mt-3">No presentations found.</div>
                )}
              </div>
            ) : (
              <div>
                <h5>Select a Presentation to Edit</h5>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {presentations.map((pres) => (
                    <Card
                      key={pres.id}
                      className="mb-2 cursor-pointer"
                      onClick={() => handleSelectPresentation(pres)}
                    >
                      <Card.Body>
                        <Card.Title>{pres.title}</Card.Title>
                        <Card.Text>{pres.description}</Card.Text>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
                <Button className="mt-3 w-100" onClick={() => setPresentations([])}>
                  Back
                </Button>
              </div>
            )}
          </Modal.Body>
        </Modal>

        {isSaving && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(255,255,255,0.7)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)'
          }}>
            <RBSpinner animation="border" role="status" style={{ width: '4rem', height: '4rem' }}>
              <span className="visually-hidden">Saving...</span>
            </RBSpinner>
            <div style={{ marginLeft: '1.5rem', fontSize: '1.5rem', color: '#333' }}>Saving...</div>
          </div>
        )}

        <div className="toolbar presentation-toolbar p-2 border-bottom d-flex align-items-center" style={{ backgroundColor: "var(--bs-offcanvas-bg)" }}>
          <div className="presentation-title-wrapper">
            {editingTitle ? (
              <Form.Control
                className="presentation-title-input"
                type="text"
                value={presentation.title}
                onChange={e => setPresentation({ ...presentation, title: e.target.value })}
                onBlur={() => setEditingTitle(false)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    setEditingTitle(false);
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                autoFocus
                style={{ fontSize: "1.5rem", fontWeight: "bold", border: "none", boxShadow: "none", paddingLeft: 0 }}
              />
            ) : (
              <h3 className="presentation-title-text" onClick={() => setEditingTitle(true)} style={{ cursor: 'pointer' }}>
                {presentation.title || "Untitled Slideshow"}
              </h3>
            )}
          </div>

          <div className="ms-auto d-flex align-items-center">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tip-change-thumb">Change Thumbnail</Tooltip>}
            >
              <Button
                variant="outline-secondary"
                size="sm"
                className="me-2"
                onClick={() => fileInputRef.current.click()}
              >
                <FontAwesomeIcon icon={faCamera} className="me-2 fa-icon-only-spacer" />
                <span className="btn-label">Change Thumbnail</span>
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip id="tip-save">Save</Tooltip>}>
              <Button
                variant="success"
                onClick={handleSave}
                disabled={isSaveDisabled}
                className="me-2"
              >
                <FontAwesomeIcon icon={faSave} className="me-2 fa-icon-only-spacer" />
                <span className="btn-label">Save</span>
              </Button>
            </OverlayTrigger>
            {presentationUrl && (
              <OverlayTrigger placement="top" overlay={<Tooltip id="tip-preview">Preview slideshow</Tooltip>}>
                <Button
                  variant="outline-primary"
                  disabled={!presentationUrl}
                  onClick={(e) => {
                    if (presentationUrl) {
                      window.open(presentationUrl, '_blank');
                    }
                    e.currentTarget.blur();
                  }}
                  className="me-2"
                >
                  <FontAwesomeIcon icon={faEye} className="me-2 fa-icon-only-spacer" />
                  <span className="btn-label">Preview slideshow</span>
                </Button>
              </OverlayTrigger>
            )}
            <OverlayTrigger placement="top" overlay={<Tooltip id="tip-details">Details</Tooltip>}>
              <Button
                variant="outline-secondary"
                onClick={() => setShowDetailsModal(true)}
                className="me-2"
              >
                <FontAwesomeIcon icon={faInfo} className="me-2 fa-icon-only-spacer" />
                <span className="btn-label">Details</span>
              </Button>
            </OverlayTrigger>
            <Form.Select
              value={presentation.privacy}
              onChange={(e) => setPresentation({ ...presentation, privacy: e.target.value })}
              className="me-2"
              style={{ width: 'auto' }}
            >
              <option value="unlisted">Unlisted</option>
              <option value="public">Public</option>
            </Form.Select>
            {presentation.id && (
              <OverlayTrigger
                placement="top"
                delay={{ show: 1500, hide: 250 }}
                overlay={<Tooltip id="tooltip-share-presentation">Share</Tooltip>}
              >
                <Button variant="outline-secondary" className="ms-2" onClick={() => setShowShareModal(true)}>
                  <FontAwesomeIcon icon={faPaperPlane} />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </div>

        <div className="main-content" style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          <div
            className="slide-sidebar"
            style={{ width: "250px", borderRight: "1px solid var(--bs-border-color)", overflowY: "auto", padding: "1rem", WebkitOverflowScrolling: 'touch' }}
            tabIndex={0}
            onKeyDown={e => {
              if (presentation.slides.length === 0) return;
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedSlideIndex(idx => Math.max(0, idx - 1));
              } else if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedSlideIndex(idx => Math.min(presentation.slides.length - 1, idx + 1));
              }
            }}
          >
            <div className="slide-thumbnails">
              {debouncedSlides.map((slide, index) => {
                return (
                  <React.Fragment key={index}>
                    {dropIndicator?.index === index && dropIndicator?.position === 'before' && (
                      <div style={{ height: '4px', backgroundColor: '#0d6efd', borderRadius: '2px', margin: '2px 0' }} />
                    )}
                    <div
                      ref={el => slideRefs.current[index] = el}
                      className={
                        "slide-thumbnail mb-2 p-2 border rounded d-flex align-items-center"
                      }
                      style={{
                        cursor: "pointer",
                        border: index === selectedSlideIndex ? "2px solid #0d6efd" : "1px solid var(--bs-border-color)",
                        boxShadow: index === selectedSlideIndex ? "0 0 0 2px #0d6efd33" : undefined,
                        background: index === selectedSlideIndex ? "#e7f1ff" : undefined,
                        transition: "border 0.2s, box-shadow 0.2s, background 0.2s",
                        opacity: index === draggedSlideIndex ? 0.5 : 1,
                        transform: index === draggedSlideIndex ? "scale(0.95)" : "scale(1)"
                      }}
                      onClick={() => setSelectedSlideIndex(index)}
                      draggable
                      onDragStart={e => {
                        e.stopPropagation();
                        setDraggedSlideIndex(index);
                        e.dataTransfer.effectAllowed = "move";
                        
                        // Create a custom drag image to show only the slide thumbnail
                        const dragImage = e.currentTarget.cloneNode(true);
                        dragImage.style.transform = 'scale(0.8)';
                        dragImage.style.opacity = '0.8';
                        dragImage.style.border = '2px solid #0d6efd';
                        dragImage.style.borderRadius = '8px';
                        dragImage.style.backgroundColor = 'white';
                        dragImage.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                        dragImage.style.pointerEvents = 'none';
                        dragImage.style.position = 'absolute';
                        dragImage.style.top = '-1000px';
                        dragImage.style.left = '-1000px';
                        dragImage.style.zIndex = '9999';
                        
                        document.body.appendChild(dragImage);
                        e.dataTransfer.setDragImage(dragImage, 125, 40); // Center the drag image
                        
                        // Clean up the drag image after a short delay
                        setTimeout(() => {
                          if (document.body.contains(dragImage)) {
                            document.body.removeChild(dragImage);
                          }
                        }, 100);
                      }}
                      onDragOver={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (index === draggedSlideIndex) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const midY = rect.top + rect.height / 2;
                        if (e.clientY < midY) {
                          setDropIndicator({ index, position: 'before' });
                        } else {
                          setDropIndicator({ index, position: 'after' });
                        }
                      }}
                      onDragLeave={e => {
                        e.stopPropagation();
                        setDropIndicator(null);
                      }}
                      onDrop={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (draggedSlideIndex === null || dropIndicator === null) return;

                        let targetIndex = dropIndicator.index;
                        if (dropIndicator.position === 'after') {
                          targetIndex += 1;
                        }
                        if (draggedSlideIndex < targetIndex) {
                          targetIndex -= 1;
                        }

                        setPresentation(prev => {
                          const slides = [...prev.slides];
                          const [moved] = slides.splice(draggedSlideIndex, 1);
                          slides.splice(targetIndex, 0, moved);
                          return { ...prev, slides };
                        });

                        setSelectedSlideIndex(targetIndex);
                        setDraggedSlideIndex(null);
                        setDropIndicator(null);
                      }}
                      onDragEnd={e => {
                        e.stopPropagation();
                        setDraggedSlideIndex(null);
                        setDropIndicator(null);
                      }}
                    >
                      <div className="me-2 text-muted" style={{ width: '20px', textAlign: 'right' }}>
                        {index + 1}
                      </div>
                      <div style={{ flex: 1, position: 'relative' }}>
                        {(slide.type === 'custom_text' || slide.slide_type === 'custom_text') ? (
                          <div style={{
                            width: '100%',
                            paddingTop: '56.25%', // 16:9 Aspect Ratio
                            position: 'relative',
                            backgroundColor: '#f0f0f0',
                            pointerEvents: 'none',
                            overflow: 'hidden',
                            border: '1px solid #ddd'
                          }}>
                            <iframe
                              srcDoc={
                                (hoveredPopupIndex === index && thumbnailHtmlCache[index]?.withPopup)
                                  ? thumbnailHtmlCache[index].withPopup
                                  : thumbnailHtmlCache[index]?.noPopup
                              }
                              title={`slide-thumb-${index}`}
                              style={{
                                position: 'absolute',
                                top: '0',
                                left: '0',
                                width: '1280px',
                                height: '720px',
                                border: 'none',
                                transform: 'scale(0.15)',
                                transformOrigin: 'top left'
                              }}
                              scrolling="no"
                              sandbox="allow-same-origin allow-scripts"
                            />
                          </div>
                        ) : (
                          <div style={{
                            width: '100%',
                            paddingTop: '56.25%', // 16:9 Aspect Ratio
                            position: 'relative',
                            backgroundColor: '#f0f0f0',
                            border: '1px solid #ddd'
                          }}>
                            <Image
                              src={slide.thumbnail || "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg"}
                              alt={`Slide ${index + 1}`}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          </div>
                        )}
                        {slide.slideState?.showPopup && (
                          <FontAwesomeIcon
                            icon={faCommentDots}
                            style={{
                              position: 'absolute',
                              bottom: '5px',
                              right: '5px',
                              color: 'white',
                              backgroundColor: 'rgba(0, 0, 0, 0.6)',
                              borderRadius: '50%',
                              padding: '4px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              zIndex: 10, // Ensure icon is clickable
                            }}
                            title="This slide has a popup message"
                            onMouseEnter={() => setHoveredPopupIndex(index)}
                            onMouseLeave={() => setHoveredPopupIndex(null)}
                          />
                        )}
                      </div>
                    </div>
                    {dropIndicator?.index === index && dropIndicator?.position === 'after' && (
                      <div style={{ height: '4px', backgroundColor: '#0d6efd', borderRadius: '2px', margin: '2px 0' }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="add-slide-button mt-3">
              <Button
                variant="outline-primary"
                className="w-100"
                onClick={(e) => {
                  handleAddCustomTextSlide();
                  e.currentTarget.blur();
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Slide
              </Button>
            </div>
          </div>

          <div className="slide-preview" style={{ flex: 1, padding: "2rem", overflow: "auto" }}>
            {presentation.slides.length === 0 ? (
              <div className="empty-state text-center" style={{ marginTop: "20%" }}>
                <h3>No slides yet</h3>
                <p>Click "Add Slide" to start creating your presentation</p>
              </div>
            ) : (
              <div className="current-slide d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 400 }}>
                {presentation.slides[selectedSlideIndex]?.slide_type === 'custom_text' ? (
                  <CustomTextSlideEditor
                    key={presentation.slides[selectedSlideIndex].id || selectedSlideIndex}
                    ref={ref => {
                      slideEditorsRef.current[selectedSlideIndex] = ref;
                      currentSlideEditorRef.current = ref;
                    }}
                    slideState={presentation.slides[selectedSlideIndex].slideState}
                    onSlideStateChange={handleSlideStateChange}
                    onOpenVisualPicker={() => { setModalView("my-visuals"); setShowAddSlideModal(true); }}
                    onIframeSelect={handleIframeSelect}
                    embedded={true}
                  />
                ) : isVideoSlide(presentation.slides[selectedSlideIndex]) ? (
                  <video
                    src={presentation.slides[selectedSlideIndex].visualization_url}
                    controls
                    style={{ width: "100%", maxWidth: 900, height: 500, border: "1px solid #ccc", borderRadius: 8, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
                    className="mb-3"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : presentation.slides[selectedSlideIndex]?.visualization_url ? (
                  <iframe
                    src={presentation.slides[selectedSlideIndex].visualization_url}
                    title={presentation.slides[selectedSlideIndex]?.title || `Slide ${selectedSlideIndex + 1}`}
                    style={{ width: "100%", maxWidth: 900, height: 500, border: "1px solid #ccc", borderRadius: 8, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
                    className="mb-3"
                    allowFullScreen
                  />
                ) : (
                  <Image
                    src={presentation.slides[selectedSlideIndex]?.thumbnail || "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg"}
                    alt={presentation.slides[selectedSlideIndex]?.title || `Slide ${selectedSlideIndex + 1}`}
                    style={{ maxWidth: "100%", maxHeight: 350, borderRadius: 8, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
                    className="mb-3"
                    fluid
                  />
                )}
                {presentation.slides[selectedSlideIndex]?.slide_type !== 'custom_text' && (
                  <div style={{ width: "100%", maxWidth: 900 }}>
                    <Form.Group className="mb-3">
                      <Form.Label>Title</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter slide intro title"
                        value={presentation.slides[selectedSlideIndex]?.header || presentation.slides[selectedSlideIndex]?.title || ""}
                        onChange={e => handleTitleChange(selectedSlideIndex, e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Popup text (delete or leave blank to hide)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Enter slide intro text"
                        value={presentation.slides[selectedSlideIndex]?.intro || ""}
                        onChange={e => handleTextChange(selectedSlideIndex, e.target.value)}
                      />
                    </Form.Group>
                  </div>
                )}
                {presentation.slides[selectedSlideIndex]?.visualization_url && !isVideoSlide(presentation.slides[selectedSlideIndex]) && (
                  <a
                    href={presentation.slides[selectedSlideIndex].visualization_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-secondary mt-2"
                  >
                    Open Visual
                  </a>
                )}
              </div>
            )}
          </div>

        </div>

        {showAddSlideModal && (
          <Modal
            id="add-slide-modal"
            size="lg"
            show={showAddSlideModal}
            onHide={() => {
              setShowAddSlideModal(false);
              setModalView("options");
            }}
            style={{ visibility: modalView === "my-visuals" ? "hidden" : "visible" }}
          >
            <Modal.Header
              closeButton
              onHide={() => {
                setShowAddSlideModal(false);
                setModalView("options");
              }}
            >
              <Modal.Title>
                {modalView === "options"
                  ? "Add New Slide"
                  : modalView === "create"
                    ? "Create a New Visual"
                    : modalView === "my-visuals"
                      ? "Your Visuals"
                      : "Add New Slide"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>{renderModalContent()}</Modal.Body>
          </Modal>
        )}
        {showAddSlideModal && modalView === "my-visuals" && (
          <VisualPickerModal
            show={true}
            onHide={() => { setModalView('options'); setShowAddSlideModal(false); }}
            onSelect={handleVisualSelectedOrCreated}
            onBackToOptions={() => setModalView('options')}
            userInfo={userInfo}
            visCache={visCache}
            owner={isOrg ? "team" : "self"}
            defaultView="my-visuals"
            lightTheme={true}
          />)}

        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Deletion</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this slide?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => handleDeleteSlide(slideToDelete)}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Presentation Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={presentation.title}
              onChange={e => setPresentation({ ...presentation, title: e.target.value })}
              className="fw-bold"
              style={{ fontSize: "1.2rem" }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={presentation.description}
              onChange={(e) => setPresentation({ ...presentation, description: e.target.value })}
            />
          </Form.Group>
          {presentation.slides[selectedSlideIndex]?.type !== 'custom_text' && (
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                label="Show Visual QR Codes"
                checked={presentation.show_visual_qrs}
                onChange={(e) => setPresentation({ ...presentation, show_visual_qrs: e.target.checked })}
                disabled={!(presentation.slides[selectedSlideIndex]?.type === 'legacy_visual' ||
                  presentation.slides[selectedSlideIndex]?.visualization_url)}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {showShareModal && (
        <ShareModal
          show={showShareModal}
          onHide={() => setShowShareModal(false)}
          url={presentation.url}
          qr={presentation.qr_url}
          id={presentation.id}
          isPresentation={true}
        />
      )}
    </>
  );
};

export default CreatePresentationPage;